import { createYuccaDigInSession } from './yuccaDigIn'
import { healSyncedTimeline } from '../resolveSession'
import type { CameraBeat, ScenarioObject, ScenarioSession } from '../types'

/** Slices of the Yucca dig-in. Times stay on the same clock as the full script. */
export const YUCCA_GAS_LEAK_ID = 'yucca-residential-gas-leak'
export const YUCCA_ROAD_CLOSURE_ID = 'yucca-police-road-closure'
export const YUCCA_FIRE_EVAC_ID = 'yucca-fire-evacuation'

const GAS_START = '2026-09-22T10:50:00-05:00'
const GAS_END = '2026-09-22T11:00:00-05:00'
const ROADS_START = '2026-09-22T11:00:00-05:00'
const ROADS_END = '2026-09-22T11:08:00-05:00'
const FIRE_START = '2026-09-22T11:08:00-05:00'
const FIRE_END = '2026-09-22T11:18:00-05:00'

/**
 * Keep the camera holds from the full story that are already in effect during
 * this chapter, plus the moves that happen inside it. A later framing that
 * belongs to this chapter (the school shot after evacuation) is pulled in
 * when the chapter includes that evacuation.
 */
function applyChapterCamera(session: ScenarioSession): void {
  const startIso = session.storyStartTime
  const endIso = session.storyEndTime
  if (!startIso || !endIso) return
  const beats = [...(createYuccaDigInSession().cameraBeats ?? [])].sort(
    (a, b) => Date.parse(a.at) - Date.parse(b.at),
  )
  const start = Date.parse(startIso)
  const end = Date.parse(endIso)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return
  let active: CameraBeat | undefined
  const during: CameraBeat[] = []
  let upcoming: CameraBeat | undefined
  for (const beat of beats) {
    const t = Date.parse(beat.at)
    if (!Number.isFinite(t)) continue
    if (t <= start) active = beat
    else if (t <= end) during.push(beat)
    else if (!upcoming) upcoming = beat
  }
  const points: CameraBeat[] = []
  if (active) {
    session.camera = { lng: active.lng, lat: active.lat, zoom: active.zoom }
    points.push({ ...active, at: startIso })
  }
  for (const beat of during) points.push({ ...beat })
  const pullsClosingShot = session.objects.some((object) => object.id === 'evac-1')
  if (pullsClosingShot && upcoming && Date.parse(upcoming.at) - end <= 5 * 60_000) {
    points.push({ ...upcoming, at: endIso })
  }
  session.cameraBeats = points
}

function inWindow(iso: string | undefined, start: string, end: string): boolean {
  if (!iso) return false
  const t = Date.parse(iso)
  return Number.isFinite(t) && t >= Date.parse(start) && t <= Date.parse(end)
}

function sliceBoard(
  objectIds: readonly string[],
  eventIds: readonly string[],
  start: string,
  end: string,
): ScenarioSession {
  const full = createYuccaDigInSession()
  const objects = new Set(objectIds)
  const events = new Set(eventIds)
  return {
    ...full,
    storyStartTime: start,
    storyEndTime: end,
    objects: full.objects.filter((object) => objects.has(object.id)).map((object) => ({ ...object })),
    timeline: full.timeline.filter((event) => events.has(event.id)).map((event) => ({ ...event })),
    comments: (full.comments ?? []).filter((comment) => inWindow(comment.createdAt, start, end)),
    cameraBeats: (full.cameraBeats ?? []).filter((beat) => inWindow(beat.at, start, end)),
  }
}

function withoutMotion(object: ScenarioObject): ScenarioObject {
  const next: ScenarioObject = { ...object }
  delete next.keyframes
  delete next.visibilityKeyframes
  return next
}

function describe(object: ScenarioObject, label: string, description: string): ScenarioObject {
  const next: ScenarioObject = { ...object, label, description }
  if (object.type === 'label') {
    next.params = { ...object.params, text: label }
  }
  return next
}

/** Wall clock in America/Chicago. Story beats are stored as UTC instants. */
function chicagoHM(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const hour = parts.find((part) => part.type === 'hour')?.value ?? ''
  const minute = parts.find((part) => part.type === 'minute')?.value ?? ''
  return `${hour}:${minute}`
}

export function createResidentialGasLeakSession(): ScenarioSession {
  // Object-first: backhoe / leak / engine sync cards (engine collapsed to on-scene).
  const session = sliceBoard(['backhoe-1', 'pipe-1', 'engine-1'], [], GAS_START, GAS_END)
  session.id = YUCCA_GAS_LEAK_ID
  session.title = 'Residential gas leak'
  session.sessionType = 'whiteboard'
  session.boardComplexity = 'everything'
  session.intro = 'A crew strikes a gas line at Yucca & Elm. One engine responds.'
  session.summary =
    'Backhoe strikes a gas line at Yucca & Elm. The leak shows at 10:52. Engine 1 appears on scene at 10:59.'
  session.comments = []
  const arriveAt = '2026-09-22T10:59:00-05:00'
  session.objects = session.objects.map((object) => {
    if (object.id === 'backhoe-1') {
      return describe(
        withoutMotion(object),
        'Backhoe',
        'Excavation equipment on site when the line is struck.',
      )
    }
    if (object.id === 'pipe-1') {
      return describe(
        withoutMotion(object),
        'Gas leak',
        'Underground gas line struck. Release on the distribution line at Yucca & Elm.',
      )
    }
    if (object.id !== 'engine-1') return object
    const last = object.keyframes?.[object.keyframes.length - 1]
    const arrived = withoutMotion(object)
    if (last) arrived.pose = { lng: last.lng, lat: last.lat }
    arrived.startsAt = arriveAt
    arrived.label = 'Engine 1'
    arrived.description =
      'Dispatched from Court / Kaiser. On Yucca just east of Eagle Mountain Drive at 10:59.'
    return arrived
  })
  applyChapterCamera(session)
  const beats = session.cameraBeats
  if (beats?.length) {
    const arrive = beats.find((beat) => Date.parse(beat.at) === Date.parse(arriveAt))
    if (arrive && beats[0]) {
      beats[0].lng = arrive.lng
      beats[0].lat = arrive.lat
      beats[0].zoom = arrive.zoom
    }
  }
  return healSyncedTimeline(session)
}

export function createPoliceRoadClosureSession(): ScenarioSession {
  // Roads Include-on (sync spine); LE + banner stay map-only.
  const session = sliceBoard(
    [
      'road-elm-desert',
      'road-yucca-kaiser',
      'road-em-baseline',
      'le-1',
      'le-2',
      'le-3',
      'banner-roads',
    ],
    [],
    ROADS_START,
    ROADS_END,
  )
  session.id = YUCCA_ROAD_CLOSURE_ID
  session.title = 'Police response and road closure'
  session.sessionType = 'whiteboard'
  session.boardComplexity = 'everything'
  session.intro = 'Officers close three roads around the Yucca & Elm dig-in.'
  session.summary =
    'At 11:01, officers close Elm at Desert, Yucca at Kaiser, and Eagle Mountain at Baseline.'
  session.comments = []
  session.objects = session.objects.map((object) => {
    if (object.id === 'road-elm-desert') {
      return describe(object, 'Elm @ Desert', 'Closed to through traffic. Appears at 11:01.')
    }
    if (object.id === 'road-yucca-kaiser') {
      return describe(object, 'Yucca @ Kaiser', 'Closed to through traffic on the east approach. Appears at 11:01.')
    }
    if (object.id === 'road-em-baseline') {
      return describe(
        object,
        'Eagle Mountain @ Baseline',
        'Closed to through traffic south of the scene. Appears at 11:01.',
      )
    }
    if (object.id === 'le-1') {
      return describe(object, 'LE - Elm @ Desert', 'Officer holding the Elm at Desert closure. On scene at 11:01.')
    }
    if (object.id === 'le-2') {
      return describe(object, 'LE - Yucca @ Kaiser', 'Officer holding the Yucca at Kaiser closure. On scene at 11:01.')
    }
    if (object.id === 'le-3') {
      return describe(
        object,
        'LE - Eagle Mountain @ Baseline',
        'Officer holding the Eagle Mountain at Baseline closure. On scene at 11:01.',
      )
    }
    if (object.id === 'banner-roads') {
      return describe(
        object,
        '⚠  ROADS CLOSED  ⚠\nElm @ Desert\nYucca @ Kaiser\nEM @ Baseline',
        'Notice listing the three closed intersections.',
      )
    }
    return object
  })
  const closureFrame = { lng: -115.47378, lat: 33.85188, zoom: 15.4 }
  session.camera = closureFrame
  session.cameraBeats = [{ at: ROADS_START, ...closureFrame }]
  return healSyncedTimeline(session)
}

export function createFireEvacuationSession(): ScenarioSession {
  // Fire + evac sync; spread is glue (radius growth has no second object card).
  const session = sliceBoard(['school-1', 'fire-1', 'evac-1'], ['ev-spread'], FIRE_START, FIRE_END)
  session.id = YUCCA_FIRE_EVAC_ID
  session.title = 'Fire escalation and structure evacuation'
  session.sessionType = 'walkthrough'
  session.boardComplexity = 'everything'
  session.intro = 'Ignition at the Yucca & Elm dig-in. The fire grows toward the school.'
  session.summary =
    'The fire starts at 11:08 and grows southeast along Yucca. The school is evacuated at 11:15.'
  session.comments = []
  session.objects = session.objects.map((object) => {
    if (object.id === 'school-1') {
      return describe(
        object,
        'School',
        'Eagle Mountain School, southeast of the dig-in. The evacuation zone appears at 11:15.',
      )
    }
    if (object.id === 'fire-1') {
      return describe(
        object,
        'Ignition at dig-in',
        'Fire at the dig-in after the backhoe move. Grows southeast along Yucca toward the school.',
      )
    }
    if (object.id === 'evac-1') {
      return describe(
        object,
        'Evacuation - school',
        'Evacuation zone around the school campus as fire pushes southeast on Yucca.',
      )
    }
    return object
  })
  applyChapterCamera(session)
  const beats = session.cameraBeats ?? []
  const ignition = beats.find((beat) => chicagoHM(beat.at) === '11:09')
  const later = beats.filter((beat) => {
    const hm = chicagoHM(beat.at)
    return hm === '11:14' || hm === '11:15'
  })
  if (ignition) {
    session.camera = { lng: ignition.lng, lat: ignition.lat, zoom: ignition.zoom }
    session.cameraBeats = [{ ...ignition, at: FIRE_START }, ...later]
  }
  return healSyncedTimeline(session)
}
